import { onMounted, onUnmounted } from "vue";
import { fetchAuthSession, signOut } from "aws-amplify/auth";
import { redirectToRoute, routeIsLogin } from "@/router";
import { showAlert } from "@/methods/errorHandling";
import { AuthRouteNames } from "@/constants/routes";
import { usePiniaStore } from "@/stores/store";
import { useOrganisationStore } from "@/stores/organisationStore";
import { configSentryError } from "@/methods/sentry";

export const endInactiveUserSession = (
    timeoutMinutes = 5,
    showWarningDialog: (visible: boolean, countdown?: number) => void,
) => {
    /**
     * Manage user session timeout due to inactivity
     * @param {number} timeoutMinutes - inactivity timeout in minutes
     * @param {function} showWarningDialog - function to show/hide warning dialog with optional countdown
     * @returns {object} - contains staySignedIn method to reset timer when user chooses to stay signed in
     */
    let inactivityTimer: ReturnType<typeof setTimeout>;
    let logoutTimer: ReturnType<typeof setInterval>;
    const warningDuration = 60; // seconds
    let isWarningVisible = false; // Flag to track if warning pop up is visible
    const pStore = usePiniaStore();
    const organisationStore = useOrganisationStore();

    const logout = async (): Promise<void> => {
        /**
         * Log out the user and redirect to login page.
         * @returns {Promise<void>}
         */
        try {
            await signOut({ global: true });
        } finally {
            stopInactivityTracking();
            organisationStore.clearOrganisationContext(pStore.getUserEmail);
            pStore.setIsAuthenticated(false);
            if (!routeIsLogin()) {
                showAlert("alerts.expiredSession");
                redirectToRoute({ name: AuthRouteNames.LOGIN });
            }
        }
    };

    const showSessionWarning = (): void => {
        /**
         * Show session timeout warning dialog and start countdown to logout.
         * @returns {void}
         */
        let countdown = warningDuration;
        isWarningVisible = true; // mark the warning pop up as visible
        showWarningDialog(true, countdown);

        logoutTimer = setInterval(() => {
            // Update countdown every second
            countdown -= 1;
            if (countdown <= 0) {
                clearInterval(logoutTimer);
                isWarningVisible = false; // mark the warning pop up as not visible
                showWarningDialog(false);
                logout();
            } else {
                showWarningDialog(true, countdown);
            }
        }, 1000);
    };

    const resetInactivityTimer = (): void => {
        /**
         * Reset the inactivity timer. If warning dialog is visible, do not reset until the user clicks.
         *  @returns {void}
         */
        if (isWarningVisible) return;

        clearTimeout(inactivityTimer);
        clearInterval(logoutTimer);
        showWarningDialog(false);

        inactivityTimer = setTimeout(
            () => {
                if (pStore.getIsAuthenticated) {
                    showSessionWarning();
                }
            },
            timeoutMinutes * 60 * 1000,
        );
    };

    const stopInactivityTracking = (): void => {
        /**
         * Stop tracking user inactivity and clear timers
         * @returns {void}
         */
        clearTimeout(inactivityTimer);
        clearInterval(logoutTimer);
        ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach(
            (evt) => window.removeEventListener(evt, resetInactivityTimer),
        );
    };

    onMounted(() => {
        /**
         * Set up event listeners to track user activity and reset inactivity timer
         * @returns {void}
         */
        resetInactivityTimer();
        ["mousemove", "keydown", "click", "scroll", "touchstart"].forEach(
            (evt) => window.addEventListener(evt, resetInactivityTimer),
        );
    });

    onUnmounted(() => {
        /**
         * Clean up event listeners and timers on component unmount
         * @returns {void}
         */
        stopInactivityTracking();
    });

    const staySignedIn = (): void => {
        /**
         * Handle user choosing to stay signed in
         * @returns {void}
         */
        isWarningVisible = false; // reset flag
        resetInactivityTimer();
    };

    return { staySignedIn };
};

/**
 * Uses the auth integration to return user's email address
 * from the idToken of the current session
 * @returns {Promise<string>} - The email address from the current session, or an empty string if not available
 */
export const getEmailFromCurrentSession = async (): Promise<string> => {
    try {
        const session = await fetchAuthSession();
        const email = session.tokens?.idToken?.payload.email;
        return typeof email === "string" ? email : "";
    } catch (error) {
        configSentryError(
            "sessionManagement.ts",
            "getEmailFromCurrentSession",
            `Error while trying get the username from the current session: ${error}`,
        );
        return "";
    }
};
